import { create } from "zustand";
import { useChartsStore } from "./chart/chartStore";
import useBookByPriceStore from "./bookByPriceStore";

/**
 * Group Linking Protocol - Central Store
 *
 * This store manages symbol synchronization between panels via group linking.
 * Panels can register as receivers (single-symbol panels) and emit symbol changes
 * to propagate selections across the group.
 *
 * See docs/architecture/group-linking-protocol.md for full documentation.
 */
const useGroupLinkStore = create((set, get) => ({
  // Registry of receivers by group
  // { [group]: [{ id, setSymbol }] }
  receivers: {},

  /**
   * Register a receiver (single-symbol panel)
   * @param {number|null} group - Group number (1-5) or null for no group
   * @param {string} id - Unique panel identifier
   * @param {function} setSymbol - Callback to change panel's symbol
   * @returns {function} Cleanup function to unregister
   */
  registerReceiver: (group, id, setSymbol) => {
    if (!group) return () => {}; // null group = not registered

    set((state) => {
      const groupReceivers = state.receivers[group] || [];
      // Avoid duplicates
      if (groupReceivers.some((r) => r.id === id)) return state;

      return {
        receivers: {
          ...state.receivers,
          [group]: [...groupReceivers, { id, setSymbol }],
        },
      };
    });

    // Return cleanup function
    return () => get().unregisterReceiver(group, id);
  },

  /**
   * Remove a receiver from the registry
   * @param {number|null} group - Group number
   * @param {string} id - Panel identifier to remove
   */
  unregisterReceiver: (group, id) => {
    if (!group) return;

    set((state) => ({
      receivers: {
        ...state.receivers,
        [group]: (state.receivers[group] || []).filter((r) => r.id !== id),
      },
    }));
  },

  /**
   * Propagate symbol to all receivers in the group (except source)
   * @param {number|null} group - Group to propagate to
   * @param {string} symbol - Symbol to propagate
   * @param {string|null} sourceId - Source panel ID to exclude from propagation
   */
  propagateSymbol: (group, symbol, sourceId = null) => {
    if (!group || !symbol) return;

    // 1. Notify mounted receivers via callbacks
    const receivers = get().receivers[group] || [];
    const receiverIds = new Set(receivers.map((r) => r.id));
    receivers.forEach((receiver) => {
      if (receiver.id !== sourceId) {
        receiver.setSymbol(symbol);
      }
    });

    // 2. Update unmounted charts (e.g. unvisited tabs) directly via store
    const { charts, changeChartSymbol } = useChartsStore.getState();
    charts.forEach((chart) => {
      if (
        chart.group === group &&
        chart.id !== sourceId &&
        !receiverIds.has(chart.id) &&
        chart.symbol !== symbol
      ) {
        // Group-link propagation to unmounted charts — not direct engagement.
        changeChartSymbol(chart.id, symbol, { skipEngagement: true });
      }
    });

    // 3. Update any unmounted BookByPrice instances that are in the same group.
    const bbpState = useBookByPriceStore.getState();
    Object.entries(bbpState.instances).forEach(([instanceId, inst]) => {
      if (
        inst.group === group &&
        sourceId !== instanceId &&
        !receiverIds.has(instanceId) &&
        inst.symbol !== symbol
      ) {
        bbpState.setInstanceSymbol(instanceId, symbol);
      }
    });
  },

  /**
   * Update a receiver's group (re-registers in new group)
   * @param {number|null} oldGroup - Previous group
   * @param {number|null} newGroup - New group
   * @param {string} id - Panel identifier
   * @param {function} setSymbol - Callback to change panel's symbol
   * @returns {function} Cleanup function
   */
  updateReceiverGroup: (oldGroup, newGroup, id, setSymbol) => {
    get().unregisterReceiver(oldGroup, id);
    return get().registerReceiver(newGroup, id, setSymbol);
  },
}));

export default useGroupLinkStore;
